import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL, ENDPOINTS, postData } from '../constants/api';
import { message, notification } from 'antd';
import AutoCompleteInput from './AutoCompleteInput';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { filterRow } from '../utils/ktcUtils';
import { decodeToken } from '../utils/TokenUtils';

const SalesJsonTable = ({
  data,
  reportHeader,
  reportFooter,
  link,
  token,
  executionTime,
  setData,
}) => {
  const navigate = useNavigate();
  const [columns, setColumns] = useState([]);
  const userlevel = decodeToken(token, 'userLevel');
  
  // Step 1: Define the state to track which rows have been processed
  const [processedRows, setProcessedRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showApiUpdateButton, setShowApiUpdateButton] = useState(false);
  // Initial state with the original data passed as a prop
  const [originalData, setOriginalData] = useState(data);
  const [filteredData, setFilteredData] = useState(data); // Filtered data to show based on search query
  const [failedData, setFailedData] = useState([]);

  const updateApiValues = async () => {
    setLoading(true);

    const successfulRows = [];
    const failedRows = [];

    try {
      for (let i = 0; i < filteredData.length; i++) {
        const row = filteredData[i];
        const updatedRow = { ...row };
        const fetchPromises = [];
        let expression;

        for (const columnKey in row) {
          const parts = columnKey.split('~');

          if (parts[2] === 'api_url' && parts.length >= 3) {
            const targetKey = parts[3];
            const targetKeyExp = parts[4];

            const originalUrl = row[columnKey];
            //expression = '5*6=7';
            // console.log('expression', expression);
            fetchPromises.push(
              axios
                .get(`${API_URL}/${ENDPOINTS.SYS}/proxy`, {
                  params: { url: originalUrl },
                  timeout: 5000,
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                })
                .then((response) => {
                  const newValue = response.data?.[targetKey];
                  // console.log(
                  //   'newValue:',
                  //   newValue,
                  //   'response.data:',
                  //   response.data
                  // );
                  if (newValue !== undefined) {
                    const targetColumn = Object.keys(row).find(
                      (k) => k.split('~')[3] === targetKey
                    );

                    if (targetColumn) {
                      updatedRow[targetColumn] = newValue;
                    }
                  } else {
                    // ✅ If targetKey not found, treat as error
                    // console.log(
                    //   `Target key "${targetKey}" missing from response for ${originalUrl}`
                    // );
                    updatedRow[`error_${targetKey}`] = true;
                  }
                })
                .catch((err) => {
                  console.log(`Failed to fetch ${originalUrl}`, err.message);
                  updatedRow[`error_${targetKey}`] = true;
                })
            );
          }
        }

        await Promise.allSettled(fetchPromises);

        // ✅ After API fetches, calculate using your function
        // calculateExpression(
        //   expression, // you should define it before
        //   i, // current row index
        //   updatedRow, // current row
        //   filteredData, // original data or updatedData
        //   setFilteredData, // setter
        //   true // supportNegative maybe true (or your flag)
        // );
        const hasError = Object.keys(updatedRow).some((key) =>
          key.startsWith('error_')
        );

        if (hasError) {
          failedRows.push(updatedRow);
        } else {
          successfulRows.push(updatedRow);
        }
      }

      setFilteredData(successfulRows);
      setOriginalData(successfulRows);

      setFailedData(failedRows);
    } catch (error) {
      console.error('Unexpected error:', error);
    } finally {
      setLoading(false);
    }
  };

  // console.log('filteredData:', filteredData);

  // Create a ref for the input element
  const inputRef = useRef(null);
  const [discount, setDiscount] = useState(0);
  const [bankId, setBankId] = useState(0);
  const [bankOptions, setBankOptions] = useState();

//   console.log('selectedRows:', selectedRows);
  const totalKey = columns?.find((col) => col.key === 'total')?.key || 'total';
  const selectedRowIndexes = Array.from(selectedRows);

  const selectedRowsArray = Array.from(selectedRowIndexes)
    .map((rowIndex) => filteredData[rowIndex])
    .filter((row) => row !== undefined); // <<< this line prevents undefined

  const subTotal = selectedRowsArray.reduce((sum, row) => {
    // console.log('row:', row, 'totalKey:', totalKey);
    const rowTotal = Number(row[totalKey]) || 0;
    return sum + rowTotal;
  }, 0);

  const vatCommission = 0;

  const [paid, setPaid] = useState(subTotal);
  const vat = (subTotal - discount) * vatCommission;

  const balance = subTotal - Number(discount) + vat - paid;

  useEffect(() => {
    // Fetch column definitions and data from the API
    const columns = Object.keys(data[0]);

    //columsn with dropdown
    const cols = columns.map((col) => {
      const parts = col.split('~');
      const columnName = col;
      const header = parts[0]; // Display label
      const key = parts[1] || parts[0]; // Key used for row data
      const type = parts[2]; // 'editable', 'dropdown', 'hide'
      const inputId = parts[3]; // Used to fetch dropdown options
      const colIndex = parts[4];

      const isDropdown = type?.includes('dropdown');
      const isCheckbox = type?.includes('checkbox');
      const isEditable = type === 'editable' || type === 'api_url';
      const isHidden = type === 'hide';

      return {
        columnName,
        header,
        key,
        type,
        inputId,
        colIndex,
        isDropdown,
        isCheckbox,
        editable: isEditable,
        visible: !isHidden,
        dropdownOptions: isDropdown ? [] : null,
      };
    });

    setColumns(cols);

    // check if has api_url column and show button to call update api url
    if (data?.length > 0) {
      const firstRow = data[0];
      const hasApiUrlColumn = Object.keys(firstRow).some(
        (key) => key.split('~')[2] === 'api_url'
      );
      setShowApiUpdateButton(hasApiUrlColumn);
      // console.log('button shows: ', hasApiUrlColumn);
    } else {
      // console.log('button not shows');
    }

    const fetchAllDropdowns = async (cols) => {
      for (const col of cols) {
        if ((col.isDropdown || col.isCheckbox) && col.inputId) {
          // console.log('col:', col);
          const url = `${API_URL}/${ENDPOINTS.SYS}/get-dropdown/${col.inputId}`;

          try {
            const response = await axios.get(url, {
              headers: {
                Authorization: token ? `Bearer ${token}` : '',
              },
            });
            const options = response.data;
            //console.log('url:', url, 'response:', response.data);

            setColumns((prevColumns) =>
              prevColumns.map((c) =>
                c.key === col.key ? { ...c, dropdownOptions: options } : c
              )
            );
          } catch (err) {
            console.error(`Dropdown fetch failed for ${col.inputId}`, err);
          }
        }
      }
    };

    const fetchBankDropdowns = async () => {
      const url = `${API_URL}/${ENDPOINTS.SYS}/get-dropdown/${link.update_link_id}`;

      try {
        const response = await axios.get(url, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        });
        const options = response.data;
        //console.log('url:', url, 'response:', response.data);
        setBankOptions(options);
      } catch (err) {
        console.error(
          `Dropdown fetch failed for bank input id: ${link.update_link_id}`,
          err
        );
      }
    };

    if (cols.length) {
      // console.log('length is true');
      fetchAllDropdowns(cols);
    } else {
      // console.log('length is false');
    }

    fetchBankDropdowns();
  }, []);
  // ✅ Sync `paid` automatically:
  useEffect(() => {
    setPaid(subTotal - discount + vat);
  }, [subTotal, discount]); // Runs every time subtotal or discount changes
  const [search, setSearch] = useState('');

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(e.target.value);

    // Filter data based on the search query
    const filtered = originalData.filter((row) =>
      Object.values(row).some((val) =>
        val?.toString().toLowerCase().includes(value.toLowerCase())
      )
    );
    setFilteredData(filtered);
  };

  const [loading, setLoading] = useState(false);

  const updateRowFromString = (rowIndex, str) => {
    console.log('📥 Incoming row index:', rowIndex);
    console.log('📥 Incoming update string:', str);

    setFilteredData((currentFiltered) => {
      console.log('🔍 Current filteredData before update:', currentFiltered);

      // Copy current state
      const updatedFiltered = [...currentFiltered];
      const targetRow = { ...updatedFiltered[rowIndex] };

      console.log('✅ Target row before update:', targetRow);

      // Convert update string into object
      const updates = Object.fromEntries(
        str.split(',').map((pair) => {
          const [k, v] = pair.split(':');
          return [k.trim(), v.trim()];
        })
      );

      console.log('📦 Parsed updates object:', updates);

      // Apply updates to matching keys
      Object.keys(targetRow).forEach((key) => {
        const secondPart = key.split('~')[1];
        console.log(`   🔎 Checking key "${key}" → secondPart "${secondPart}"`);

        if (updates.hasOwnProperty(secondPart)) {
          console.log(
            `   ✏️ Updating "${key}" from "${targetRow[key]}" to "${updates[secondPart]}"`
          );
          targetRow[key] = updates[secondPart];
        }
      });

      // Replace row in array
      updatedFiltered[rowIndex] = targetRow;

      console.log('🆕 Updated row after update:', targetRow);
      console.log('📤 Updated filteredData after update:', updatedFiltered);

      return updatedFiltered; // update state
    });
  };

  const handleEdit = async (e, rowIndex2, colData, row, colIndex) => {
    try {
      const columnName = colData.columnName;
      const updatedData = [...data];
      const updatedFiltered = [...filteredData];

      // ---------- Extract new value based on input type ----------
      let text = e.target.textContent;
      let str = '';

      if (
        colData.type.includes('dropdown') ||
        colData.type === 'autocomplete' ||
        colData.type === 'input'
      ) {
        text = e.target.value;
        str = e.target.info;
      }

      // ---------- Utility: find matching row index ----------
      const findMatchingRowIndex = (filteredRow, originalData) => {
        return originalData.findIndex((origRow) => {
          const keysToCompare = Object.keys(filteredRow).filter(
            (k) => !k.includes('~options') // ignore injected dropdown options
          );
          return keysToCompare.every(
            (key) => filteredRow[key] === origRow[key]
          );
        });
      };

      const rowIndex = findMatchingRowIndex(row, updatedData);
      if (rowIndex === -1) return;

      // ---------- Avoid redundant update ----------
      if (updatedData[rowIndex][columnName] === text) return;

      // ---------- Handle load-dropdown ----------
      if (colData.type === 'load-dropdown') {
        try {
          setLoading(true);
          const url = `${API_URL}/${ENDPOINTS.SYS}/load-dropdown/${colData.inputId}/${text}`;
          const response = await axios.get(url, {
            headers: {
              Authorization: token ? `Bearer ${token}` : '',
            },
          });

          const newOptions = response.data.results;
          const targetColIndex = parseInt(colData.colIndex, 10);
          const optionsKey = `${columns[targetColIndex].columnName}~options`;

          // Update dropdown options for both datasets
          updatedData[rowIndex][optionsKey] = newOptions;
          updatedFiltered[rowIndex2][optionsKey] = newOptions;
        } catch (error) {
          console.error('Error fetching dropdown:', error);
        } finally {
          setLoading(false);
        }
      }
      //update from info
      // Convert string to an object { quantity: 12, price: 1.50, Purchase_Cost: 0.40 }
      if (e.target.info) {
        const updates = Object.fromEntries(
          str.split(',').map((pair) => {
            const [k, v] = pair.split(':');
            return [k.trim(), v.trim()];
          })
        );

        // Update matching keys
        Object.keys(updatedData[rowIndex]).forEach((key) => {
          const parts = key.split('~');
          const secondPart = parts[1]; // second part of the key

          if (updates.hasOwnProperty(secondPart)) {
            updatedData[rowIndex][key] = updates[secondPart];
          }
        });
      }

      // ---------- Update value in both datasets ----------
      updatedData[rowIndex][columnName] = text;
      updatedFiltered[rowIndex2][columnName] = text;

      console.log('Updated:', columnName, ':', text);
      console.log('Updated Row:', updatedData[rowIndex]);

      setData(updatedData);
      setFilteredData(updatedFiltered);

      // ---------- Handle expression-based calculations ----------
      const expression = columnName?.split('~')[3]?.split(',');
      const isSupportNegative = columnName?.split('~')[4] ?? '1';

      if (expression) {
        const uniqueExpressions = [...new Set(expression)];
        uniqueExpressions.forEach((expr) => {
          if (expr) {
            calculateExpression(
              expr,
              rowIndex,
              row,
              updatedData,
              setData,
              isSupportNegative
            );
          }
        });

        // ---------- Auto-submit when autotick is present ----------
        // if (uniqueExpressions[0]?.includes('autotick')) {
        //   handleSubmit(row, rowIndex);
        // }
      }
    } catch (err) {
      alert(err);
      console.error('Edit error:', err);
    }
  };

  console.log('filtered Data:', filteredData);
  function calculateExpression(
    expression,
    rowIndex,
    row,
    updatedData,
    setData,
    isSupportNegative
  ) {
    // Parse the string like "7-6=8"
    const parts = expression?.split('=');
    const leftSide = parts[0]; // "7-6"
    const rightSide = parts[1]; // "8" - column where result should be set

    // Create a regex that matches any of the delimiters: -, +, *, /
    const delimiterRegex = /[-+*/]/;

    // Check if the string contains any of the specified delimiters
    const match = leftSide.match(delimiterRegex);

    if (!match) {
      return;
    }

    // The first matched delimiter will be in the 'match' array
    const delimiter = match[0];
    // console.log('Found delimiter:', delimiter);

    // Split the string by the found delimiter and convert to integers
    const [col1, col2] = leftSide
      .split(delimiter) // Split by the found delimiter
      .map((index) => parseInt(index.trim())); // Convert to integers

    // console.log('col1:', col1);
    // console.log('col2:', col2);

    const col3 = rightSide;
    // Convert the object keys into an array
    const keys = Object.keys(row);

    // Get the values from the row data
    let value1 = row[keys[col1]];
    let value2 = row[keys[col2]];

    // console.log('keys:', keys);

    // console.log(`col1 - ${keys[col1]} Value at index ${col1} : ${value1}`);
    // console.log(`col2 - ${keys[col2]} Value at index ${col2} : ${value2}`);

    // Perform the subtraction
    var result = 0;

    if (isNaN(parseFloat(value1))) {
      notification.error({
        message: `${keys[col1].split('~')[0]} Value: '${value1}'  at index ${col1} is not a number`,
      });
    }
    if (isNaN(parseFloat(value2))) {
      notification.error({
        message: `${keys[col2].split('~')[0]} Value : '${value2}' at index ${col2}  is not a number`,
      });
    }

    if (delimiter === '-') {
      result = value1 - value2;
      if (result < 0 && isSupportNegative === '0') {
        notification.error({ message: 'The difference is negative.' });
        return;
      }
    } else if (delimiter === '+') {
      result = parseFloat(value1) + parseFloat(value2);
    } else if (delimiter === '*') {
      result = parseFloat(value1) * parseFloat(value2);
    } else if (delimiter === '/') {
      result = parseFloat(value1) / parseFloat(value2);
    }

    updatedData[rowIndex][keys[col3]] =
      value1 === '' || value2 === '' ? '' : result.toFixed(2);

    setData(updatedData);
  }

  const handleSubmit = async (row, rowIndex) => {
    const result = await submitRow(row, rowIndex);

    if (result.success) {
      setProcessedRows((prev) => [...prev, rowIndex]);

      const updatedData = originalData.filter(
        (dataRow) => JSON.stringify(dataRow) !== JSON.stringify(row)
      );
      setOriginalData(updatedData);

      const updatedFilteredData = filteredData.filter(
        (dataRow) => JSON.stringify(dataRow) !== JSON.stringify(row)
      );
      setFilteredData(updatedFilteredData);

      if (inputRef.current) {
        inputRef.current.focus();
      }
      setSearch('');
      notification.success({ message: result.message });
    } else {
      notification.error({ message: result.message });
    }
  };

  const renderCell = ({ col, row, rowIndex, colIndex, handleEdit }) => {
    const value = row[col.columnName] || '';

    // Autocomplete
    if (col.type === 'autocomplete') {
      return (
        <AutoCompleteInput
          col={col}
          value={value}
          rowIndex={rowIndex}
          colIndex={colIndex}
          onChange={(id, name, info = '') => {
            console.log('load info:', info, 'row:', row);

            handleEdit(
              { target: { value: id, info: info } },
              rowIndex,
              col,
              row,
              colIndex
            );
            //updateRowFromString(rowIndex, info);

            // row[col.columnName] = name;
          }}
        />
      );
    }

    // Dropdown
    if (col.isDropdown) {
      const options =
        row[`${col.columnName}~options`] || col.dropdownOptions || [];
      return (
        <select
          className="form-select form-select-sm"
          value={value}
          onChange={(e) => handleEdit(e, rowIndex, col, row, colIndex)}
        >
          <option value="">-- Select --</option>
          {options.map((opt, i) => (
            <option key={i} value={opt.id}>
              {opt.name}
            </option>
          ))}
        </select>
      );
    }

    if (col.isCheckbox && Array.isArray(col.dropdownOptions)) {
      const selectedValues = Array.isArray(row[col.columnName])
        ? row[col.columnName]
        : [];
      console.log('selected values: ', selectedValues);
      return (
        <div className="d-flex flex-row">
          {col.dropdownOptions.map((opt) => (
            <label key={opt.id} className="form-check-label me-3">
              <input
                type="checkbox"
                className="form-check-input me-1"
                value={opt.id}
                checked={selectedValues.map(String).includes(String(opt.id))}
                onChange={(e) => handleEdit(e, rowIndex, col, row, colIndex)}
              />
              {opt.name}
            </label>
          ))}
        </div>
      );
    }

    // Standard input
    if (col.type === 'input' && col.inputId) {
      return (
        <input
          type={col.inputId}
          className="form-control form-control-sm"
          value={value}
          onChange={(e) => handleEdit(e, rowIndex, col, row, colIndex)}
        />
      );
    }

    // Editable content
    if (col.editable) {
      return (
        <div
          contentEditable
          suppressContentEditableWarning
          onBlur={(e) => handleEdit(e, rowIndex, col, row, colIndex)}
        >
          {value}
        </div>
      );
    }

    // Default: just show value
    return value;
  };

  // console.log(
  //   'data length: ',
  //   data?.length,
  //   ' filtered length: ',
  //   filteredData.length
  // );

  // console.log('filtered data:', filteredData);

  const exportToExcel = (data, title) => {
    // Step 1: Map data to have only first part of keys
    const mappedData = data.map((row) => {
      const newRow = {};
      for (const key in row) {
        const firstPart = key.split('~')[0]; // get the first part before ~
        newRow[firstPart] = row[key];
      }
      return newRow;
    });

    // Step 2: Create sheet
    const ws = XLSX.utils.json_to_sheet(mappedData);

    // Step 3: Create workbook and export
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'data');
    XLSX.writeFile(wb, title + '.xlsx');
  };
  // ✅ Filter a row: remove ~options keys & extract proper keys
  // const filterRow = (row) => {
  //   return Object.keys(row).reduce((acc, key) => {
  //     const parts = key.split('~');
  //     if (parts[parts.length - 1] === 'options') return acc;
  //     if (parts.length >= 2) {
  //       acc[parts[1]] = row[key];
  //     }
  //     return acc;
  //   }, {});
  // };

  // ✅ Submit a single row: returns { success, message, row, rowIndex }
  const submitRow = async (row, rowIndex) => {
    console.log('row2:', row);
    let filteredRow = filterRow(row);
    filteredRow = {
      ...filteredRow,
      sub_total: subTotal,
      vat: vat,

      discount: discount,

      paid: paid,
      bank_id: bankId,
    };
    // console.log('filteredRow2 and added D.V.P:', filteredRow);
    const route = filteredRow.api_route;
    delete filteredRow.api_route;

    const insertUrl = `${API_URL}/${ENDPOINTS.SYS}/create/${route}`;

    try {
      const response = await axios.post(insertUrl, filteredRow, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      return {
        success: response.data.success === 1,
        message: response.data.message,
        row: filteredRow,
        rowIndex,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message,
        row,
        rowIndex,
      };
    }
  };

  const handleSubmitAll = async () => {
    const selectedRowIndexes = Array.from(selectedRows);
    const rows = Array.from(selectedRows)
  .map(i => filteredData[i])

  .filter(Boolean);
    if (selectedRowIndexes.length === 0) {
      notification.warning({ message: 'No rows selected to submit.' });
      return;
    }
    let apiRoute;
    const rowsFiltered=rows.map(row=>filterRow(row));
    const rowsToSubmit=rowsFiltered.map(row=>{
    const {api_route,...rest}=row;
    apiRoute=api_route;
    return {
        ...rest,
        sub_total: subTotal,
        vat: vat,
        discount: discount,
        paid: paid,
        bank_id: bankId,
    }
    });
    //  console.log("revised rows: ",rowsFiltered);
    //  console.log("rows to submit: ",rowsToSubmit);
    const json={json:rowsToSubmit};
    console.log("json: ",json);
    try {
     var insertUrl = `${API_URL}/${ENDPOINTS.SYS}/create-json/${apiRoute}`;

    const response = await postData(insertUrl,json);
     if (userlevel === 'developer') {
            console.log(
      'url:',
      insertUrl,
      'request:',
     rowsToSubmit,
      'response:',
      response
    );
    if (response.success===1 || response.success==='1') {
        notification.success({
          message: response.message,
        });
    }else{
        notification.error({
              message: response.message,
            });
    }
    
    if (response.success===1 || response.success==='1') {
      navigate(-1);
    }
        if (response.success===1 && response?.insertId && response?.print_route) {
        let full_route=`/${response?.print_route}/${response?.insertId}`
        window.open(full_route, "_blank");
      }
    }
    }catch(err){
         notification.error({
              message: 'Bulk submission failed',
              description: err.message,
            });
    }
    // notification.info({
    //   message: `Submit All Result`,
    //   description: `Success: ${successCount}, Failed: ${failedCount}`,
    // });
  };

  return (
    <div className=" ">
      <div className="row">
        <div className="col-9">
          {' '}
          {showApiUpdateButton && (
            <button
              onClick={updateApiValues}
              className="btn btn-danger"
              disabled={loading}
            >
              {loading ? '⏳ Updating...' : '🔄 Update API Values'}
            </button>
          )}
          <button
            onClick={() => exportToExcel(filteredData, `${link.page_title}`)}
            className="btn btn-primary"
          >
            Export Excel
          </button>
          {failedData && failedData.length > 0 && (
            <button
              onClick={() =>
                exportToExcel(failedData, `${link.page_title}-failed`)
              }
              className="btn btn-danger"
            >
              Export Errors {failedData?.length}
            </button>
          )}
        </div>
        <div className="col-3">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={handleSearch}
            className="form-control mb-3"
            style={{ maxWidth: '300px' }} // Optional: Limit max width for better alignment
            ref={inputRef} // Attach the ref here
          />
        </div>
      </div>
      <div className="container-fluid">
        <table className="table table-bordered">
          <thead className="table-dark">
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedRows.size === filteredData.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedRows(new Set(filteredData.map((_, i) => i)));
                    } else {
                      setSelectedRows(new Set());
                    }
                  }}
                />
              </th>

              {columns
                .filter((col) => col.visible && col.key !== 'color') // Ignore "color" column
                .map((col) => (
                  <th key={col.key} className="text-capitalize">
                    {col.header}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {filteredData &&
              filteredData.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={
                    row.color
                      ? `row-color-${row.color.trim().toLowerCase()}`
                      : ''
                  }
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedRows.has(rowIndex)}
                      onChange={() => {
                        setSelectedRows((prev) => {
                          const updated = new Set(prev);
                          if (updated.has(rowIndex)) {
                            updated.delete(rowIndex);
                          } else {
                            updated.add(rowIndex);
                          }
                          return updated;
                        });
                      }}
                    />
                  </td>
                  {columns
                    .filter((col) => col.visible && col.key !== 'color')
                    .map((col, colIndex) => (
                      <td
                        key={col.key}
                        style={
                          col.editable ||
                          col.isDropdown ||
                          col.type === 'autocomplete'
                            ? { backgroundColor: '#f9f9f9' }
                            : {}
                        }
                      >
                        {renderCell({
                          col,
                          row,
                          rowIndex,
                          colIndex,
                          handleEdit,
                        })}
                      </td>
                    ))}
                </tr>
              ))}
          </tbody>
        </table>
        <div className="d-flex flex-column align-items-end mt-3">
          <div className="mb-1">
            <strong>Sub Total:</strong> $
            <input
              type="number"
              value={subTotal.toFixed(2)}
              style={{ width: '300px' }}
              readOnly
            />
          </div>
          <div className="mb-1">
            <strong>Discount:</strong>
            {' $'}
            <input
              type="number"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              style={{ width: '300px' }}
            />
          </div>
          <div className="mb-1">
            <strong>VAT:</strong> $
            <input
              type="number"
              value={vat.toFixed(2)}
              style={{ width: '300px' }}
              readOnly
            />
          </div>
          <div className="mb-1">
            <strong>Paid:</strong>
            {' $'}
            <input
              type="number"
              value={paid}
              onChange={(e) => setPaid(e.target.value)}
              style={{ width: '300px' }}
            />
          </div>
          <div className="mb-1">
            <strong>Balance:</strong>
            {' $'}
            <input
              type="number"
              value={balance}
              // onChange={(e) => setPaid(e.target.value)}
              style={{ width: '300px' }}
            />
          </div>

          <div className="mb-1">
            <strong>Bank:</strong>{' '}
            <select
              value={bankId}
              onChange={(e) => setBankId(e.target.value)}
              style={{ width: '300px' }}
            >
              <option value="">Select Account</option>
              {bankOptions &&
                bankOptions.length &&
                bankOptions.map((bank) => (
                  <option key={bank.id} value={bank.id}>
                    {bank.name}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <div className="text-end">
          <button className="btn btn-success" onClick={handleSubmitAll}>
            Save Selected - {selectedRows.size} records
          </button>
        </div>
      </div>

      {executionTime !== null && (
        <p>
          Request executed in: {executionTime.toFixed(2)} sec - Total rows:
          {filteredData && filteredData.length} rows
        </p>
      )}
    </div>
  );
};

export default SalesJsonTable;
